import {IM_CODES} from "../../constants/imcodes";
import WORD_USAGE from "../../constants/word-usage.json";

const PARTIAL_MATCH_MIN_LEN = 1;
const IMAGE_MATCH_MAX = 14;
const PARTIAL_MATCH_MAX_IMAGES = IMAGE_MATCH_MAX;
const PARTIAL_MATCH_PER_KEY_MAX = 2;
const SIMILAR_TEXT_MAX = 24;

export default class Searcher{

	private items:string[];
	private itemsWithCaps:string[];
	private usageByItem:Record<string, number>;

    constructor(){
		this.itemsWithCaps=Object.keys(IM_CODES);
		this.items=[];
		for(let i=0;i<this.itemsWithCaps.length;i++){
			this.items[i]=this.itemsWithCaps[i].toLowerCase();
		}
		this.usageByItem={};
		for(const [word, count] of Object.entries(WORD_USAGE)){
			const wordLower=word.toLowerCase();
			this.usageByItem[wordLower]=(this.usageByItem[wordLower]||0)+Number(count);
		}
    }

    findImages(searchText:string){
		const exactRes=this.findImagesExact(searchText);
		if(exactRes.length>0){
			const cappedExactRes=exactRes.slice(0,IMAGE_MATCH_MAX);
			const exactCodes=new Set<string>();
			for(let i=0;i<cappedExactRes.length;i++){
				exactCodes.add(cappedExactRes[i][1]);
			}
			const remainingPartialSlots=IMAGE_MATCH_MAX-cappedExactRes.length;
			if(remainingPartialSlots<=0){
				return cappedExactRes;
			}
			const partialRes=this.findImagesPartialPrefix(searchText,{
				excludeExact:true,
				seenCodes:exactCodes,
				maxImages:remainingPartialSlots,
			});
			return [...cappedExactRes,...partialRes];
		}
		return this.findImagesPartialPrefix(searchText,{maxImages:IMAGE_MATCH_MAX});
    }

	private findImagesExact(searchText:string):string[][]{
		let searchRes:string[][]=[];
		let indexFound=this.items.indexOf(searchText);
		if(indexFound!==-1){
			let searchTextWithCaps=this.itemsWithCaps[indexFound];
			for(let i=0;i<IM_CODES[searchTextWithCaps].length;i++){
				searchRes.push([searchTextWithCaps,IM_CODES[searchTextWithCaps][i]])
			}

			//this.items can have duplicate entries when IM_CODES has one capitalized and one uncapitalized version of the same word
			//the duplicate is always immediated after 
			//this ensures that the ims for the duplicates are also added to searchRes
			if(indexFound+1<this.items.length&&this.items[indexFound+1]===searchText){
				let searchTextWithCaps=this.itemsWithCaps[indexFound+1];
				for(let i=0;i<IM_CODES[searchTextWithCaps].length;i++){
					searchRes.push([searchTextWithCaps,IM_CODES[searchTextWithCaps][i]])
				}
			}
		}
		return searchRes;
	}

	private findImagesPartialPrefix(searchText:string,options?:{excludeExact?:boolean; seenCodes?:Set<string>; maxImages?:number}):string[][]{
		if(searchText.length<PARTIAL_MATCH_MIN_LEN){
			return [];
		}
		const excludeExact=Boolean(options?.excludeExact);
		const maxImages=options?.maxImages??PARTIAL_MATCH_MAX_IMAGES;
		const indices:number[]=[];
		for(let i=0;i<this.items.length;i++){
			const item=this.items[i];
			if(!item.startsWith(searchText)){
				continue;
			}
			if(excludeExact&&item===searchText){
				continue;
			}
			indices.push(i);
		}
		indices.sort((a,b)=>{
			const usageDiff=(this.usageByItem[this.items[b]]||0)-(this.usageByItem[this.items[a]]||0);
			if(usageDiff!==0){
				return usageDiff;
			}
			const lenDiff=this.items[b].length-this.items[a].length;
			if(lenDiff!==0){
				return lenDiff;
			}
			return a-b;
		});

		const searchRes:string[][]=[];
		const seenCodes=options?.seenCodes??new Set<string>();
		type PartialWordMatch={
			keyCaps:string;
			codes:string[];
			nextCodeIndex:number;
		};
		const wordMatches:PartialWordMatch[]=[];
		for(const idx of indices){
			const keyCaps=this.itemsWithCaps[idx];
			const codes=IM_CODES[keyCaps];
			wordMatches.push({
				keyCaps,
				codes,
				nextCodeIndex:0,
			});
		}

		// Pass 1: preserve variety with up to N images per matched word.
		outer:for(const wordMatch of wordMatches){
			let addedFromKey=0;
			while(wordMatch.nextCodeIndex<wordMatch.codes.length){
				const code=wordMatch.codes[wordMatch.nextCodeIndex];
				wordMatch.nextCodeIndex++;
				if(seenCodes.has(code)){
					continue;
				}
				seenCodes.add(code);
				searchRes.push([wordMatch.keyCaps,code]);
				addedFromKey++;
				if(searchRes.length>=maxImages){
					break outer;
				}
				if(addedFromKey>=PARTIAL_MATCH_PER_KEY_MAX){
					break;
				}
			}
		}

		// Pass 2: if still under cap, append additional images from same matched words.
		if(searchRes.length<maxImages){
			outerSecond:for(const wordMatch of wordMatches){
				while(wordMatch.nextCodeIndex<wordMatch.codes.length){
					const code=wordMatch.codes[wordMatch.nextCodeIndex];
					wordMatch.nextCodeIndex++;
					if(seenCodes.has(code)){
						continue;
					}
					seenCodes.add(code);
					searchRes.push([wordMatch.keyCaps,code]);
					if(searchRes.length>=maxImages){
						break outerSecond;
					}
				}
			}
		}
		return searchRes;
	}
	
	findSimilarText(searchText:string){
		if(searchText.length===0){
			return [];
		}
		type SimilarTextCandidate={
			item:string;
			prefixMatchLen:number;
			usage:number;
			index:number;
		};
		const candidatesMap=new Map<string, SimilarTextCandidate>();
		for(let i=0;i<this.items.length;i++){
			const item=this.items[i];
			if(item===searchText){
				continue;
			}
			const prefixMatchLen=this.getPrefixMatchLen(item,searchText);
			if(prefixMatchLen===0){
				continue;
			}
			const usage=this.usageByItem[item]||0;
			const existing=candidatesMap.get(item);
			if(!existing){
				candidatesMap.set(item,{item,prefixMatchLen,usage,index:i});
				continue;
			}
			if(usage>existing.usage){
				existing.usage=usage;
			}
			if(i<existing.index){
				existing.index=i;
			}
		}
		const candidates=[...candidatesMap.values()];
		candidates.sort((a,b)=>{
			if(a.prefixMatchLen!==b.prefixMatchLen){
				return b.prefixMatchLen-a.prefixMatchLen;
			}
			if(a.usage!==b.usage){
				return b.usage-a.usage;
			}
			return a.index-b.index;
		});
		const matches:string[]=[];
		for(let i=0;i<candidates.length&&matches.length<SIMILAR_TEXT_MAX;i++){
			matches.push(candidates[i].item);
		}
		return matches;
	}

	private getPrefixMatchLen(item:string,searchText:string):number{
		const maxLen=Math.min(item.length,searchText.length);
		let i=0;
		while(i<maxLen&&item[i]===searchText[i]){
			i++;
		}
		return i;
	}
}
